// Wave director. Tracks a target "spawn budget per second" curve and an
// archetype mix that shifts over time. Spawns enemies just outside the
// camera bounds at a rate that matches the budget. Periodic "wave events"
// fire every 90 seconds — short telegraphed bursts that vary by phase.
//
// All randomness comes from ws.rng('spawn'); given a fixed seed, the spawn
// schedule is deterministic. This is what makes the smoke-run regression
// test in src/sim/smoke-run.ts possible.

import type { WorldState } from '../world-state';
import { spawnEnemyOf } from '../enemies';
import type { EnemyKind } from '../components';
import { randFloat, type Rng, pick } from '../rng';

const SPAWN_RING_PADDING = 80;
const WAVE_EVENT_INTERVAL_SEC = 90;

/** Phases: early (0-90s), mid (90-300s), late (300s+). */
export type Phase = 'early' | 'mid' | 'late';

/** Spawn budget per second, by phase. Documented difficulty curve. */
const BUDGET_PER_SEC: Record<Phase, number> = {
  early: 1.5,
  mid: 4.0,
  late: 6.5,
};

/** Archetype mix weights per phase. Weights are relative; pickWeighted normalises. */
const MIX: Record<Phase, ReadonlyArray<[EnemyKind, number]>> = {
  early: [['seeker', 1.0]],
  mid: [
    ['seeker', 0.5],
    ['shooter', 0.2],
    ['swarmer', 0.2],
    ['bomber', 0.05],
    ['elite', 0.05],
  ],
  late: [
    ['seeker', 0.25],
    ['shooter', 0.18],
    ['swarmer', 0.27],
    ['bomber', 0.15],
    ['elite', 0.15],
  ],
};

/** Lookup the active phase at a given run time (seconds). */
export function currentPhase(timeSec: number): Phase {
  if (timeSec < 90) return 'early';
  if (timeSec < 300) return 'mid';
  return 'late';
}

/** Spawn budget per second at a given time, interpolating between phases. */
export function spawnBudget(timeSec: number): number {
  if (timeSec < 90) {
    // Ramp from 1.5 → 3.0 over the first 90s so phase 0 isn't flat.
    const t = timeSec / 90;
    return BUDGET_PER_SEC.early + (BUDGET_PER_SEC.mid * 0.5 - BUDGET_PER_SEC.early) * t;
  }
  if (timeSec < 300) {
    const t = (timeSec - 90) / 210;
    return BUDGET_PER_SEC.mid * (0.75 + 0.5 * t);
  }
  // Late phase: gentle continued ramp toward late budget, then clamp.
  const t = Math.min(1, (timeSec - 300) / 600);
  return BUDGET_PER_SEC.mid + (BUDGET_PER_SEC.late - BUDGET_PER_SEC.mid) * t;
}

export interface WaveDirectorOptions {
  /** Multiplier on spawn budget — set < 1 on touch devices for lighter waves. */
  budgetMultiplier?: number;
}

export class WaveDirector {
  private accumulator = 0;
  private nextEventAt = WAVE_EVENT_INTERVAL_SEC;
  private eventCount = 0;
  private readonly budgetMul: number;

  constructor(opts: WaveDirectorOptions = {}) {
    this.budgetMul = opts.budgetMultiplier ?? 1;
  }

  update(ws: WorldState, dt: number): void {
    const rng = ws.rng('spawn');
    const budget = spawnBudget(ws.time) * this.budgetMul;
    this.accumulator += dt * budget;
    while (this.accumulator >= 1) {
      this.accumulator -= 1;
      const kind = pickKind(currentPhase(ws.time), rng);
      this.spawnAt(ws, kind, rng);
    }

    // Wave events.
    if (ws.time >= this.nextEventAt) {
      this.runWaveEvent(ws, rng);
      this.nextEventAt += WAVE_EVENT_INTERVAL_SEC;
      this.eventCount++;
    }
  }

  private spawnAt(ws: WorldState, kind: EnemyKind, rng: Rng): void {
    const { x, y } = spawnPoint(ws, rng);
    spawnEnemyOf(ws, kind, x, y);
  }

  private runWaveEvent(ws: WorldState, rng: Rng): void {
    const pattern = pickWavePattern(currentPhase(ws.time), rng);
    if (pattern === 0) {
      // Swarmer rush: 16 swarmers from one cardinal direction.
      const { x, y } = spawnPoint(ws, rng);
      for (let i = 0; i < 16; i++) {
        const jitterX = x + randFloat(rng, -60, 60);
        const jitterY = y + randFloat(rng, -60, 60);
        spawnEnemyOf(ws, 'swarmer', jitterX, jitterY);
      }
    } else if (pattern === 1) {
      // Shooter ring: 6 shooters in a circle outside bounds.
      const r = Math.max(ws.bounds.maxX, ws.bounds.maxY) + SPAWN_RING_PADDING;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        spawnEnemyOf(ws, 'shooter', Math.cos(a) * r, Math.sin(a) * r);
      }
    } else {
      // Mini-boss: one elite, one of every other kind for support.
      const { x, y } = spawnPoint(ws, rng);
      spawnEnemyOf(ws, 'elite', x, y);
      const supports: EnemyKind[] = ['seeker', 'shooter', 'bomber', 'swarmer'];
      for (const k of supports) {
        const p = spawnPoint(ws, rng);
        spawnEnemyOf(ws, k, p.x, p.y);
      }
    }
  }
}

/** Phase-weighted weights for wave event patterns: [swarmer rush, shooter ring, mini-boss]. */
const WAVE_EVENT_WEIGHTS: Record<Phase, [number, number, number]> = {
  early: [0.7, 0.2, 0.1],
  mid: [0.33, 0.34, 0.33],
  late: [0.2, 0.3, 0.5],
};

/**
 * Pick a wave-event pattern index (0=swarmer rush, 1=shooter ring, 2=mini-boss)
 * using phase-weighted seeded random selection.
 */
export function pickWavePattern(phase: Phase, rng: Rng): 0 | 1 | 2 {
  const weights = WAVE_EVENT_WEIGHTS[phase];
  const total = weights[0] + weights[1] + weights[2];
  let r = randFloat(rng, 0, total);
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return i as 0 | 1 | 2;
  }
  return 2;
}

function pickKind(phase: Phase, rng: Rng): EnemyKind {
  const mix = MIX[phase];
  const total = mix.reduce((s, [, w]) => s + w, 0);
  let r = randFloat(rng, 0, total);
  for (const [kind, w] of mix) {
    r -= w;
    if (r <= 0) return kind;
  }
  return mix[mix.length - 1]![0];
}

function spawnPoint(ws: WorldState, rng: Rng): { x: number; y: number } {
  const b = ws.bounds;
  const sides = ['top', 'bottom', 'left', 'right'] as const;
  const side = pick(rng, sides);
  if (side === 'top') return { x: randFloat(rng, b.minX, b.maxX), y: b.minY - SPAWN_RING_PADDING };
  if (side === 'bottom')
    return { x: randFloat(rng, b.minX, b.maxX), y: b.maxY + SPAWN_RING_PADDING };
  if (side === 'left') return { x: b.minX - SPAWN_RING_PADDING, y: randFloat(rng, b.minY, b.maxY) };
  return { x: b.maxX + SPAWN_RING_PADDING, y: randFloat(rng, b.minY, b.maxY) };
}
